import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import AccountCard from "components/features/account/account-card/accountCard";
import {
  ActionIcon,
  Box,
  Space,
  rem,
  Text,
  Grid,
  Divider,
  Image,
  useMantineTheme,
  Flex,
  Title,
} from "@mantine/core";
import { IconArrowLeft, IconArrowRight, IconX } from "@tabler/icons-react";
import useRolePermission from "@/hooks/useRolePermission";
import accountData from "modules/accounts/account-list/AccountData";
import {
  NavigateOptions,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import ContractInformation from "../../account-detail/ContractInformation";
import { toCurrency } from "@/utils/common";
// import ListStatement from "../list-statement/ListStatement";
import {
  useExportTransHistoryMutation,
  useLazyGetAccountDetailQuery,
} from "@/services/transactionAPI";
import { get } from "lodash";
import dayjs from "dayjs";
import Imgtransfer from "@/assets/images/pngs/transfer1.png";
import Imgthanhtoan from "@/assets/images/pngs/money-profit1.png";
import Imgblock from "@/assets/images/pngs/blocked1.png";
import TabsCustomer from "./components/TabsCustomer";
import TabsHistory from "./components/TabsHistory";
import LockAccountDrawer from "./components/LockAccountDrawer";
import OtpModal from "@/components/common/modal/otp-modal/OtpModal";
import RoutesString from "@/pages/routesString";
import {
  useConfirmLockAccountMutation,
  useGenerateOTPMutation,
  useInitLockAccountMutation,
} from "@/services/authAPI";
import { SHA256 } from "crypto-js";
import { notifications } from "@mantine/notifications";
import ImageTick from "@/assets/images/pngs/tick-double-01.png";
import { GROUP_TYPE, RESULT_CODE, USER_ROLE_KHDN } from "@/constants/enum";

const { accountViewStatement } = accountData;

const CurrentAccountPanelDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const { state } = useLocation();
  const { can } = useRolePermission();

  const navigateWithPermission = (path: string, options?: NavigateOptions) => {
    if (!can([USER_ROLE_KHDN.MAKER, USER_ROLE_KHDN.ADMIN])) {
      notifications.show({
        withBorder: true,
        title: t("FAILURE"),
        message: t("NO_PERMISSION_ACCESS"),
        color: "red",
        icon: <IconX size={16} />,
      });
      return;
    }
    navigate(path, options);
  };
  const [fromDate, setFromDate] = useState<string>(
    dayjs().format("YYYY-MM-DD"),
  );
  const [toDate, setToDate] = useState<string>(dayjs().format("YYYY-MM-DD"));

  const [opened, setOpened] = useState(false);

  // draft là Date | null
  const [draftFrom, setDraftFrom] = useState<Date | null>(
    dayjs(fromDate).toDate(),
  );
  const [draftTo, setDraftTo] = useState<Date | null>(dayjs(toDate).toDate());

  const openPicker = () => {
    setDraftFrom(fromDate ? dayjs(fromDate, "YYYY-MM-DD").toDate() : null);
    setDraftTo(toDate ? dayjs(toDate, "YYYY-MM-DD").toDate() : null);
    setOpened(true);
  };

  const closePicker = () => setOpened(false);

  const apply = () => {
    // Không dùng onChange nữa, vì bạn đang setState trực tiếp trong component
    setFromDate(draftFrom ? dayjs(draftFrom).format("YYYY-MM-DD") : "");
    setToDate(draftTo ? dayjs(draftTo).format("YYYY-MM-DD") : "");
    setOpened(false);
  };

  const stateSourceId = get(state, "sourceId", "").toString();
  const stateAccountNo = get(state, "accountNo", "").toString();
  const { id: accountNo } = useParams<{ id: string }>();

  const [
    isOpenContractInformation,
    { open: openContractInformation, close: closeContractInformation },
  ] = useDisclosure(false);
  const { accountId } = useParams();

  const [triggerDetail, { data: getAccountDetail }] =
    useLazyGetAccountDetailQuery();
  const resultDetail = get(getAccountDetail, "body");
  // TK đã khóa ('C') — làm mờ nút "Khoá tài khoản" và chặn mở luồng khóa
  const isAccountLocked = resultDetail?.accountStatus === "C";

  // Số dư hiển thị: ưu tiên chuỗi BE trả (availableBalanceDisplay, đã kèm loại tiền
  // vd "2,937,130,361 USD"). Tách phần số & loại tiền để giữ style 2 cỡ chữ;
  // nếu BE không trả thì fallback về availableBalance + currency.
  const balanceDisplayRaw = String(
    resultDetail?.availableBalanceDisplay || "",
  ).trim();
  const balanceLastSpace = balanceDisplayRaw.lastIndexOf(" ");
  const hasBalanceDisplay = balanceLastSpace > 0;
  const balanceAmountText = hasBalanceDisplay
    ? balanceDisplayRaw.slice(0, balanceLastSpace)
    : toCurrency(Number(resultDetail?.availableBalance) || 0);
  const balanceCurrencyText = hasBalanceDisplay
    ? balanceDisplayRaw.slice(balanceLastSpace + 1)
    : resultDetail?.currency || "VND";

  const getAccountTypeLabel = (type: string) => {
    switch (type) {
      case GROUP_TYPE.CORE_BANK:
        return t("CORE_BANK");
      case GROUP_TYPE.CORE_WALLET:
        return t("CORE_WALLET");
      default:
        return type;
    }
  };

  const getStatus = (value: string) => {
    switch (value) {
      // Domain BIZ_SOURCE_LINK.STATUS: 'A' = hoạt động ('O' giữ cho tương thích), 'C' = khóa/đóng
      case "A":
      case "O":
        return t("ACCOUNT_STATUS_ACTIVE_LABEL");
      case "C":
        return t("STATE_LOCKED");
      default:
        return "";
    }
  };

  useEffect(() => {
    if (stateAccountNo) {
      triggerDetail({ id: stateAccountNo });
    }
  }, [stateAccountNo]);

  const currentData = [
    {
      value: "account summary",
      label: t("ACCOUNT_SUMMARY"),
      panelContent: (
        <Box
          style={{
            padding: "20px",
            display: "flex",
          }}
        >
          <Box
            style={{
              flex: "1",
            }}
          >
            <Box
              style={{
                paddingBottom: "20px",
              }}
            >
              <p className="text-[#000] text-[14px] font-extrabold">
                {t("TOTAL_BALANCE")}
              </p>
              <p className="text-[#2A81D0] text-[24px]">
                <span className="font-bold">{balanceAmountText}</span>{" "}
                <span className="text-[14px]">{balanceCurrencyText}</span>
              </p>
            </Box>
            <Grid>
              <Grid.Col span={6}>
                <Grid>
                  <Grid.Col span={6}>
                    <Text size="sm" color="dimmed">
                      {t("OVERDRAFT_LIMIT")}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" weight={700}>
                      {resultDetail?.overdraftLimitDisplay}
                    </Text>
                  </Grid.Col>
                </Grid>
                <Grid>
                  <Grid.Col span={6}>
                    <Text size="sm" color="dimmed">
                      {t("BLOCKED_AMOUNT")}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" weight={700}>
                      {resultDetail?.overdraftAmountDisplay}
                    </Text>
                  </Grid.Col>
                </Grid>
                <Divider
                  my="sm"
                  sx={{
                    borderTop: "1px dashed #D8DDE2",
                  }}
                />
                <Grid>
                  <Grid.Col span={6}>
                    <Text size="sm" color="dimmed">
                      {t("STATUS")}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "4px 10px",
                        borderRadius: 999,
                        // TK bị khóa: ô đỏ + icon X đỏ thay tick xanh
                        background: isAccountLocked ? "#FDE8E8" : "#E8F7EE",
                      }}
                    >
                      {isAccountLocked ? (
                        <IconX size={24} stroke={3} color="#DC2626" />
                      ) : (
                        <Image
                          src={ImageTick}
                          height={24}
                          width={24}
                          fit="contain"
                        />
                      )}

                      <Text
                        size="sm"
                        weight={600}
                        sx={{ color: isAccountLocked ? "#DC2626" : "#16A34A" }}
                      >
                        {getStatus(resultDetail?.accountStatus ?? "")}
                      </Text>
                    </Box>
                  </Grid.Col>
                </Grid>
              </Grid.Col>
            </Grid>
          </Box>
        </Box>
      ),
    },
    {
      value: "interest details",
      label: t("INTEREST_RATE_INFORMATION"),
      panelContent: (
        <Box
          style={{
            padding: "20px",
          }}
        >
          <Box
            style={{
              display: "flex",
              width: "100%",
            }}
          >
            <Box
              style={{
                flex: "1",
                paddingRight: "20px",
              }}
            >
              <Grid>
                <Grid.Col span={6}>
                  <Text size="sm" color="dimmed">
                    <p className="text-[#5E656B]">{t("INTEREST_RATE")}</p>
                  </Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" weight={700}>
                    <p className="text-[#131618] text-[16px] font-bold text-right">
                      {resultDetail?.interestRateDisplay}
                    </p>
                  </Text>
                </Grid.Col>
              </Grid>
            </Box>
            <Box
              style={{
                marginLeft: "20px",
                flex: "1",
              }}
            >
              <Grid>
                <Grid.Col span={6}>
                  <Text size="sm" color="dimmed">
                    <p className="text-[#5E656B]">{t("OVERDRAFT_INTEREST_RATE")}</p>
                  </Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" weight={700}>
                    <p className="text-[#131618] text-[16px] font-bold text-right">
                      {resultDetail?.overdraftRateDisplay}
                    </p>
                  </Text>
                </Grid.Col>
              </Grid>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      value: "account info",
      label: t("ACCOUNT_INFO"),
      panelContent: (
        <Grid>
          <Grid.Col span={6}>
            <Box pt={20}>
              {[
                {
                  label: t("ACCOUNT_NUMBER"),
                  value: resultDetail?.accountNumber,
                  flexGrow: 1,
                },
                {
                  label: t("ACCOUNT_TYPE"),
                  value: getAccountTypeLabel(resultDetail?.accountType ?? ""),
                  flexGrow: 1,
                },
                {
                  label: t("ACCOUNT_NICKNAME_CARD"),
                  value: resultDetail?.accountAlias,
                  flexGrow: 1,
                },
                {
                  label: t("PRODUCT_NAME"),
                  value: t("CURRENT_ACCOUNT"),
                  flexGrow: 1,
                },
              ].map((item, index, array) => (
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexGrow: item.flexGrow,
                    flexBasis: 0,
                    padding: "0 20px",
                    marginBottom: "10px",
                  }}
                >
                  <Text size="sm" c="#5E656B">
                    {item.label}
                  </Text>

                  <Text fw={700} size="sm" c="#131618">
                    {item.value ?? "-"}
                  </Text>
                </Box>
              ))}
            </Box>
          </Grid.Col>
          <Grid.Col span={6}>
            <Box pt={20}>
              {[
                {
                  label: t("OPENING_BRANCH"),
                  value: resultDetail?.openingBranch,
                  flexGrow: 1,
                },
                {
                  label: t("OPEN_DATE"),
                  value: resultDetail?.openDate,
                  flexGrow: 1,
                },
                {
                  label: t("CURRENT"),
                  value: resultDetail?.currency,
                  flexGrow: 2.07,
                },
              ].map((item, index, array) => (
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexGrow: item.flexGrow,
                    flexBasis: 0,
                    padding: "0 20px",
                    marginBottom: "10px",
                  }}
                >
                  <Text size="sm" c="#5E656B">
                    {item.label}
                  </Text>

                  <Text fw={700} size="sm" c="#131618">
                    {item.value ?? "-"}
                  </Text>
                </Box>
              ))}
            </Box>
          </Grid.Col>
        </Grid>
      ),
    },
  ];
  const [openedStatement, { open: openStatement, close: closeStatement }] =
    useDisclosure(false);

  const downloadFile = (url: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = url.split("/").pop() || "file.pdf";
    document.body.appendChild(link);
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
    document.body.removeChild(link);
  };

  const [openedLock, { open: openLock, close: closeLock }] =
    useDisclosure(false);

  const [openedOTP, { open: openOTP, close: closeOTP }] = useDisclosure(false);
  const [otp, setOtp] = useState("");
  const [generateOTP, { isLoading: isGeneratingOTP }] =
    useGenerateOTPMutation();

  const [lockAccount, { isLoading: isLockingAccount }] =
    useInitLockAccountMutation();
  const [confirmLockAccount] = useConfirmLockAccountMutation();
  const [otpId, setOtpId] = useState<string>("");
  const [exportTrans] = useExportTransHistoryMutation();
  useEffect(() => {
    setOtpId("");
  }, []);

  const [infoLock, setInfoLock] = useState<any>(null);

  const handleOpenLock = async () => {
    try {
      const result = await lockAccount({
        accountNumber: resultDetail?.accountNumber || "",
      }).unwrap();
      if (result.body?.resultCode === RESULT_CODE.SUCCESS) {
        const info = {
          id: result.body?.approvalProcessInfo?.approvalProcessId,
          approvalUserNames:
            result.body?.approvalProcessInfo?.approvalUserNames[0],
          numberOfApprovals:
            result.body?.approvalProcessInfo?.numberOfApprovals,
        };
        setInfoLock(info);
        openLock();
      } else {
        notifications.show({
          title: t("FAILURE"),
          message: result.body?.resultDesc || t("AN_ERROR_OCCURRED"),
          color: "red",
        });
      }
    } catch (error) {
      notifications.show({
        title: t("FAILURE"),
        message: t("AN_ERROR_OCCURRED"),
        color: "red",
      });
      console.log(error);
    }
  };

  const isLoadingLock = isGeneratingOTP || isLockingAccount;
  const handleLockAccount = async () => {
    try {
      const otpResponse = await generateOTP({
        otpType: "SMS_OTP",
        objectId: "1234",
        objectType: "tranfer",
        phoneNumber: "0987655679",
      }).unwrap();
      if (otpResponse.body?.resultCode === "0") {
        const resultOTP = otpResponse.body?.otpId;
        setOtpId(resultOTP);
        closeLock();
        openOTP();
        setOtp("");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleResendOtp = async () => {
    try {
      await generateOTP({
        otpType: "SMS_OTP",
        objectId: "1234",
        objectType: "tranfer",
        phoneNumber: "0987655679",
      }).unwrap();
    } catch (error) {
      console.log(error);
    }
  };
  const handleOtp = async () => {
    const hashedOtp = SHA256(otp).toString();
    const closeReason = localStorage.getItem("closeReason") || "";
    try {
      const result = await confirmLockAccount({
        otpId,
        otp: hashedOtp,
        closeReason,
      }).unwrap();
      if (result.body?.resultCode === "0") {
        localStorage.removeItem("closeReason");
        // closeOTP();
        navigate(RoutesString.LockDetail, {
          state: {
            dataConfirm: result.body,
          },
        });
      } else {
        notifications.show({
          title: t("ERROR"),
          message: result.body?.resultDesc || "",
          color: "red",
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      closeOTP();
    }
  };

  console.log("resultDetail", resultDetail);
  return (
    <div>
      <Flex align="center" gap={10}>
        <ActionIcon
          variant="transparent"
          onClick={() => navigate(RoutesString.AccountsList)}
          aria-label="Back to accounts"
          sx={{ cursor: "pointer" }}
        >
          <IconArrowLeft color="#000" />
        </ActionIcon>
        <Title fw={800} fz={24} py={10}>
          {t("ACCOUNTS_DETAIL")}
        </Title>
      </Flex>

      <Space h={rem(10)} />
      <Box
        style={{
          display: "flex",
          gap: "10px",
        }}
      >
        <Box
          style={{
            flex: "1",
          }}
        >
          <AccountCard
            title={resultDetail?.accountNumber}
            description={
              resultDetail?.currency
                ? `TKTT-${resultDetail?.currency}${resultDetail?.accountAlias ? `-${resultDetail?.accountAlias}` : ""}`
                : ""
            }
          />
          <TabsCustomer defaultValue="account summary" tabs={currentData} />
          <TabsHistory
            accountId={accountNo}
            defaultValue="trans"
            sourceLinkId={stateSourceId}
            title={resultDetail?.accountNumber}
            description={
              resultDetail?.currency
                ? `TKTT-${resultDetail?.currency}${resultDetail?.accountAlias ? `-${resultDetail?.accountAlias}` : ""}`
                : ""
            }
          />
        </Box>
        <Box
          style={{
            width: "540px",
            height: "270px",
            borderRadius: "10px",
            boxShadow: "0px 2px 8px 0px #638DBF1A",
            background: "#FDFDFD",
            padding: "20px",
            paddingTop: "0",
          }}
        >
          <Box
            style={{
              fontWeight: 700,
              fontSize: "18px",
              lineHeight: "25.2px",
              letterSpacing: "0%",
              padding: "18.5px",
              paddingLeft: "0",
            }}
          >
            {t("QUICK_ACTION")}
          </Box>
          <Box
            sx={{
              padding: "11px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "#F9FAFB",
              border: "1px solid #EAEAEA",
              borderRadius: "10px",
              marginBottom: "12px",
              cursor: "pointer",
            }}
            onClick={() => {
              navigateWithPermission(`/transfer-money/transfer-by-account`, {
                state: {
                  sourceLinkId: stateSourceId
                    ? Number(stateSourceId)
                    : undefined,
                  sourceNumber: accountNo,
                  accountNo: stateAccountNo || accountNo,
                },
              });
            }}
          >
            <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <Image
                src={Imgtransfer}
                width={32}
                fit="contain"
                alt="Domestic Transfer"
              />
              <Text fz="14px" fw={500}>
                {t("FUND_TRANSFER")}
              </Text>
            </Box>
            <IconArrowRight stroke={2} color="#28303F" />
          </Box>
          <Box
            sx={{
              padding: "11px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "#F9FAFB",
              border: "1px solid #EAEAEA",
              borderRadius: "10px",
              marginBottom: "12px",
              cursor: "pointer",
            }}
            onClick={() => {
              navigateWithPermission(`/manage-payment/bill-payment`, {
                state: {
                  sourceLinkId: stateSourceId
                    ? Number(stateSourceId)
                    : undefined,
                  sourceNumber: accountNo,
                  accountNo: stateAccountNo || accountNo,
                },
              });
            }}
          >
            <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <Image
                src={Imgthanhtoan}
                width={32}
                fit="contain"
                alt="Domestic Transfer"
              />
              <Text fz="14px" fw={500}>
                {t("BILL_PAYMENT")}
              </Text>
            </Box>
            <IconArrowRight stroke={2} color="#28303F" />
          </Box>
          <Box
            sx={{
              padding: "11px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "#F9FAFB",
              border: "1px solid #EAEAEA",
              borderRadius: "10px",
              marginBottom: "12px",
              // TK đã khóa: làm mờ nút, bấm vào báo noti thay vì mở luồng khóa
              cursor: isAccountLocked ? "not-allowed" : "pointer",
              opacity: isAccountLocked ? 0.5 : 1,
            }}
            onClick={() => {
              if (isAccountLocked) {
                notifications.show({
                  title: t("NOTICE"),
                  message: t("TPL_ACCOUNT_LOCKED", { p0: resultDetail?.accountNumber || "" }),
                  color: "orange",
                });
                return;
              }
              handleOpenLock();
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <Image
                src={Imgblock}
                width={32}
                fit="contain"
                alt="Domestic Transfer"
              />
              <Text fz="14px" fw={500}>
                {t("LOCK_ACCOUNT")}
              </Text>
            </Box>
            <IconArrowRight stroke={2} color="#28303F" />
          </Box>
        </Box>
      </Box>
      <ContractInformation
        opened={isOpenContractInformation}
        onClose={closeContractInformation}
        fileUrl="/pdf/file.pdf"
        onDowload={() => {
          downloadFile("/pdf/file.pdf");
        }}
      />
      <LockAccountDrawer
        opened={openedLock}
        onClose={closeLock}
        onSubmit={handleLockAccount}
        isLoading={isLoadingLock}
        title={resultDetail?.accountNumber}
        description={resultDetail?.currency ? `TKTT-${resultDetail?.currency}${resultDetail?.accountAlias ? `-${resultDetail?.accountAlias}` : ""}` : ""}
        infoLock={infoLock}
      />
      <OtpModal
        opened={openedOTP}
        onClose={closeOTP}
        onConfirm={handleOtp}
        expireTime={60}
        otp={otp}
        onChangeOtp={(value) => setOtp(value)}
        handleSendOtp={handleResendOtp}
      />
    </div>
  );
};
export default CurrentAccountPanelDetail;
